import { useCallback, useRef, useState } from 'react';

/**
 * Returns a [ref, isVisible] tuple. Attach `ref` to the element you want to observe.
 * `isVisible` becomes true as soon as the element enters the viewport and reverts when it leaves.
 *
 * `ref` is a callback ref, so observation (re)starts whenever the target node mounts or
 * unmounts — this handles conditionally-rendered targets that aren't present on first render.
 *
 * @param {IntersectionObserverInit} [options]
 * @returns {[(node: Element | null) => void, boolean]}
 */
const useOnScreen = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const ref = useCallback(node => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) {
      setIsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, optionsRef.current);

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [ref, isVisible];
};

export default useOnScreen;
